/**
 * DecorSystem.ts
 * Milestone M4: Isometric Grid Decor & Build Mode Manager.
 * Handles furniture catalog, 8x8 diamond grid placement validation, collision detection,
 * 90-degree rotation, Tier 1 -> Tier 2 -> Tier 3 upgrades, State Souvenir placement,
 * and passive Energy & Happiness recovery buff aggregation.
 */

import { GameState } from '../core/GameState';
import { EventBus } from '../core/EventBus';
import { PlacedFurniture } from '../core/Types';
import { IsometricEngine } from '../engine/IsometricEngine';
import { FLAGSHIP_STATES } from '../content/FlagshipStatesRegistry';
import {
  getSouvenirEntry,
  isSouvenirPlaced,
  isSouvenirUnlocked,
  normalizeSouvenirId,
} from '../content/StateSouvenirsRegistry';

export interface FurnitureCatalogItem {
  typeId: string;
  name: string;
  vietnameseName: string;
  tier: number;
  costUSD: number;
  category: 'BED' | 'FRIDGE' | 'DESK' | 'SOFA' | 'PLANT' | 'SOUVENIR';
  passiveEnergyBuff: number;    // % per minute
  passiveHappinessBuff: number; // % per minute
  activeUtilityDescription?: string;
  spriteKey: string;
  width: number;
  height: number;
  icon: string;
}

export interface ActiveFurnitureBonuses {
  highestBedTier: number;
  highestDeskTier: number;
  highestFridgeTier: number;
  highestSofaTier: number;
  highestPlantTier: number;
  deskPayMultiplierBonus: number;
  deskShiftPayMultiplier: number;
  deskStudyEnergyDiscount: number;
  bedNapEnergyRestore: number;
  bedNapHappinessBonus: number;
  bedSleepEnergyRestore: number;
  bedSleepHungerDrain: number;
  bedWakeUpPayBuff: boolean;
  fridgePriceDiscount: number;
  fridgeFoodPriceDiscount: number;
  fridgeBuffDurationMultiplier: number;
  fridgeFoodBuffDurationMultiplier: number;
  sofaSitHappinessRestore: number;
  sofaSitEnergyRestore: number;
  sofaTvHappinessRestore: number;
  sofaObstacleCostDiscount: number;
  sofaObstacleCostReduction: number;
  plantCheckinCashBonus: number;
  plantCheckinBonusUSD: number;
}

export const FURNITURE_CATALOG_ITEMS: FurnitureCatalogItem[] = [
  // Bed (2x1)
  {
    typeId: 'bed',
    name: 'Futon Mat',
    vietnameseName: 'Đệm Futon Đơn Giản',
    tier: 1,
    costUSD: 150,
    category: 'BED',
    passiveEnergyBuff: 0.15,
    passiveHappinessBuff: 0.2,
    activeUtilityDescription: 'Chợp mắt +40% NL, Ngủ sâu +85% NL (-10% No)',
    spriteKey: 'bed_t1',
    width: 2,
    height: 1,
    icon: '🛏️'
  },
  {
    typeId: 'bed',
    name: 'Wooden Bed Frame',
    vietnameseName: 'Giường Gỗ Sồi Ấm Cúng',
    tier: 2,
    costUSD: 1200,
    category: 'BED',
    passiveEnergyBuff: 0.35,
    passiveHappinessBuff: 0.45,
    activeUtilityDescription: 'Chợp mắt +55% NL, Ngủ sâu +100% NL (chỉ -5% No)',
    spriteKey: 'bed_t2',
    width: 2,
    height: 1,
    icon: '🛏️'
  },
  {
    typeId: 'bed',
    name: 'Luxury King Canopy Bed',
    vietnameseName: 'Giường Hoàng Gia King Size',
    tier: 3,
    costUSD: 5800,
    category: 'BED',
    passiveEnergyBuff: 0.65,
    passiveHappinessBuff: 0.8,
    activeUtilityDescription: 'Chợp mắt +75% NL (+10% Vui), Ngủ sâu +100% NL (0% đói) + Buff Sảng Khoái (+15% lương 5p)',
    spriteKey: 'bed_t3',
    width: 2,
    height: 1,
    icon: '👑'
  },

  // Fridge (1x1)
  {
    typeId: 'fridge',
    name: 'Mini Cooler',
    vietnameseName: 'Tủ Lạnh Mini Sinh Viên',
    tier: 1,
    costUSD: 140,
    category: 'FRIDGE',
    passiveEnergyBuff: 0.05,
    passiveHappinessBuff: 0.15,
    activeUtilityDescription: 'Bảo quản thức ăn chuẩn',
    spriteKey: 'fridge_t1',
    width: 1,
    height: 1,
    icon: '🧊'
  },
  {
    typeId: 'fridge',
    name: 'Standard Apartment Fridge',
    vietnameseName: 'Tủ Lạnh Gia Đình 2 Ngăn',
    tier: 2,
    costUSD: 1100,
    category: 'FRIDGE',
    passiveEnergyBuff: 0.2,
    passiveHappinessBuff: 0.35,
    activeUtilityDescription: 'Giảm 15% giá mua đồ ăn & Tăng +25% thời lượng Buff món ăn',
    spriteKey: 'fridge_t2',
    width: 1,
    height: 1,
    icon: '❄️'
  },
  {
    typeId: 'fridge',
    name: 'Smart French-Door Refrigerator',
    vietnameseName: 'Tủ Lạnh Thông Minh Cảm Ứng',
    tier: 3,
    costUSD: 4800,
    category: 'FRIDGE',
    passiveEnergyBuff: 0.45,
    passiveHappinessBuff: 0.65,
    activeUtilityDescription: 'Giảm 30% giá mua đồ ăn & Tăng +50% thời lượng Buff món ăn',
    spriteKey: 'fridge_t3',
    width: 1,
    height: 1,
    icon: '✨'
  },

  // Desk (2x1)
  {
    typeId: 'desk',
    name: 'Old Folding Table',
    vietnameseName: 'Bàn Gấp Laptop Cũ',
    tier: 1,
    costUSD: 120,
    category: 'DESK',
    passiveEnergyBuff: 0.05,
    passiveHappinessBuff: 0.1,
    activeUtilityDescription: 'Mở bảng Việc làm & Học chứng chỉ chuẩn',
    spriteKey: 'desk_t1',
    width: 2,
    height: 1,
    icon: '💻'
  },
  {
    typeId: 'desk',
    name: 'Ergonomic Standing Tech Desk',
    vietnameseName: 'Bàn Công Thái Học 2 Màn Hình',
    tier: 2,
    costUSD: 1600,
    category: 'DESK',
    passiveEnergyBuff: 0.25,
    passiveHappinessBuff: 0.4,
    activeUtilityDescription: '+12% lương mọi ca làm việc & Giảm 15% NL khi học chứng chỉ',
    spriteKey: 'desk_t2',
    width: 2,
    height: 1,
    icon: '🖥️'
  },
  {
    typeId: 'desk',
    name: 'Triple-Monitor Cyber Workstation',
    vietnameseName: 'Dàn PC Cyber 3 Màn Hình RGB',
    tier: 3,
    costUSD: 7200,
    category: 'DESK',
    passiveEnergyBuff: 0.5,
    passiveHappinessBuff: 0.75,
    activeUtilityDescription: '+28% lương mọi ca làm việc & Giảm 30% NL khi học chứng chỉ',
    spriteKey: 'desk_t3',
    width: 2,
    height: 1,
    icon: '🚀'
  },

  // Sofa (2x1)
  {
    typeId: 'sofa',
    name: 'Casual Beanbag',
    vietnameseName: 'Ghế Lười Hạt Xốp',
    tier: 1,
    costUSD: 100,
    category: 'SOFA',
    passiveEnergyBuff: 0.1,
    passiveHappinessBuff: 0.25,
    activeUtilityDescription: 'Ngồi nghỉ +15% Vui, Xem TV +35% Vui',
    spriteKey: 'sofa_t1',
    width: 2,
    height: 1,
    icon: '🛋️'
  },
  {
    typeId: 'sofa',
    name: 'Fabric Loveseat',
    vietnameseName: 'Sofa Nỉ Đôi Hiện Đại',
    tier: 2,
    costUSD: 950,
    category: 'SOFA',
    passiveEnergyBuff: 0.25,
    passiveHappinessBuff: 0.5,
    activeUtilityDescription: 'Ngồi nghỉ +25% Vui, Xem TV +50% Vui & Giảm 25% thiệt hại biến cố xấu',
    spriteKey: 'sofa_t2',
    width: 2,
    height: 1,
    icon: '🛋️'
  },
  {
    typeId: 'sofa',
    name: 'Italian Leather Recliner',
    vietnameseName: 'Sofa Da Ý Massage Cao Cấp',
    tier: 3,
    costUSD: 4500,
    category: 'SOFA',
    passiveEnergyBuff: 0.5,
    passiveHappinessBuff: 0.9,
    activeUtilityDescription: 'Ngồi nghỉ +40% Vui (+15% NL), Xem TV +70% Vui & Giảm 50% thiệt hại biến cố xấu',
    spriteKey: 'sofa_t3',
    width: 2,
    height: 1,
    icon: '💎'
  },

  // Plants (1x1)
  {
    typeId: 'plant',
    name: 'Potted Cactus',
    vietnameseName: 'Chậu Xương Rồng Sa Mạc',
    tier: 1,
    costUSD: 90,
    category: 'PLANT',
    passiveEnergyBuff: 0.05,
    passiveHappinessBuff: 0.15,
    activeUtilityDescription: '+$15 thưởng thêm mỗi lần Check-in đời thực',
    spriteKey: 'plant_cactus',
    width: 1,
    height: 1,
    icon: '🌵'
  },
  {
    typeId: 'plant',
    name: 'Monstera Deliciosa',
    vietnameseName: 'Chậu Trầu Bà Lá Xẻ',
    tier: 2,
    costUSD: 420,
    category: 'PLANT',
    passiveEnergyBuff: 0.12,
    passiveHappinessBuff: 0.3,
    activeUtilityDescription: '+$35 thưởng thêm mỗi lần Check-in đời thực',
    spriteKey: 'plant_monstera',
    width: 1,
    height: 1,
    icon: '🪴'
  },
  {
    typeId: 'plant',
    name: 'Ficus Bonsai Tree',
    vietnameseName: 'Cây Bonsai Nghệ Thuật',
    tier: 3,
    costUSD: 1800,
    category: 'PLANT',
    passiveEnergyBuff: 0.25,
    passiveHappinessBuff: 0.55,
    activeUtilityDescription: '+$80 thưởng thêm mỗi lần Check-in đời thực',
    spriteKey: 'plant_bonsai',
    width: 1,
    height: 1,
    icon: '🌳'
  }
];

export class DecorSystemManager {
  private static instance: DecorSystemManager;

  private constructor() {}

  public static getInstance(): DecorSystemManager {
    if (!DecorSystemManager.instance) {
      DecorSystemManager.instance = new DecorSystemManager();
    }
    return DecorSystemManager.instance;
  }

  public getCatalog(): FurnitureCatalogItem[] {
    return [...FURNITURE_CATALOG_ITEMS];
  }

  public getCatalogItem(typeId: string, tier: number): FurnitureCatalogItem | undefined {
    return FURNITURE_CATALOG_ITEMS.find((item) => item.typeId === typeId && item.tier === tier);
  }

  public getBaseDimensions(typeId: string): { width: number; height: number } {
    switch (typeId) {
      case 'bed':
      case 'desk':
      case 'sofa':
        return { width: 2, height: 1 };
      default:
        return { width: 1, height: 1 };
    }
  }

  public getEffectiveDimensions(typeId: string, rotation: number = 0): { width: number; height: number } {
    const base = this.getBaseDimensions(typeId);
    if (rotation === 90 || rotation === 270) {
      return { width: base.height, height: base.width };
    }
    return base;
  }

  /**
   * Checks whether an item of footprint (width x height) can be placed at (col, row)
   * within the 8x8 isometric room grid without colliding with other placed items.
   */
  public canPlace(
    col: number,
    row: number,
    width: number,
    height: number,
    excludeId?: string
  ): boolean {
    if (!Number.isInteger(col) || !Number.isInteger(row) || width <= 0 || height <= 0) {
      return false;
    }

    // Check 8x8 grid boundaries for entire footprint
    for (let c = col; c < col + width; c++) {
      for (let r = row; r < row + height; r++) {
        if (!IsometricEngine.isWithinBounds(c, r)) {
          return false;
        }
      }
    }

    // Check overlap with existing placed furniture
    const placed = GameState.getState().placedFurniture || [];
    for (const item of placed) {
      if (excludeId && item.id === excludeId) continue;

      const dims = this.getEffectiveDimensions(item.typeId, item.rotation || 0);
      const itemEndCol = item.gridCol + dims.width;
      const itemEndRow = item.gridRow + dims.height;
      const targetEndCol = col + width;
      const targetEndRow = row + height;

      const overlapsCol = col < itemEndCol && targetEndCol > item.gridCol;
      const overlapsRow = row < itemEndRow && targetEndRow > item.gridRow;

      if (overlapsCol && overlapsRow) {
        return false;
      }
    }

    return true;
  }

  /**
   * Finds the first available valid (col, row) cell for a given footprint.
   */
  public findFirstAvailableSpot(width: number, height: number): { col: number; row: number } | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if (this.canPlace(col, row, width, height)) {
          return { col, row };
        }
      }
    }
    return null;
  }

  /**
   * Purchases a new furniture item and places it on the room grid.
   */
  public placeFurniture(
    typeId: string,
    tier: number,
    col?: number,
    row?: number,
    rotation: number = 0
  ): { success: boolean; item?: PlacedFurniture; error?: string } {
    const catalogEntry = this.getCatalogItem(typeId, tier);
    if (!catalogEntry) {
      return { success: false, error: 'Vật phẩm không tồn tại trong danh mục!' };
    }

    const dims = this.getEffectiveDimensions(typeId, rotation);
    let targetCol = col;
    let targetRow = row;

    if (targetCol === undefined || targetRow === undefined) {
      const spot = this.findFirstAvailableSpot(dims.width, dims.height);
      if (!spot) {
        return { success: false, error: 'Phòng đã kín chỗ! Hãy dọn bớt hoặc sắp xếp lại.' };
      }
      targetCol = spot.col;
      targetRow = spot.row;
    }

    if (!this.canPlace(targetCol, targetRow, dims.width, dims.height)) {
      return { success: false, error: 'Vị trí đặt bị trùng hoặc vượt ngoài phạm vi phòng 8x8!' };
    }

    if (GameState.getState().stats.money < catalogEntry.costUSD) {
      return { success: false, error: `Không đủ $${catalogEntry.costUSD} USD để mua ${catalogEntry.vietnameseName}!` };
    }

    GameState.modifyMoney(-catalogEntry.costUSD);

    const newItem: PlacedFurniture = {
      id: `${typeId}_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      typeId,
      tier,
      gridCol: targetCol,
      gridRow: targetRow,
      rotation
    };

    GameState.getState().placedFurniture.push(newItem);
    this.syncPassiveBuffs();
    GameState.triggerDebouncedSave();
    EventBus.emit('FURNITURE_UPDATED', GameState.getState().placedFurniture);

    return { success: true, item: newItem };
  }

  /**
   * Places an unlocked State Souvenir onto the room grid for free.
   */
  public placeSouvenir(
    souvenirId: string,
    col?: number,
    row?: number
  ): { success: boolean; item?: PlacedFurniture; error?: string } {
    const state = GameState.getState();
    const normalizedSouvenirId = normalizeSouvenirId(souvenirId);
    if (!normalizedSouvenirId) {
      return { success: false, error: 'Mã kỷ vật không hợp lệ!' };
    }

    const hasUnlocked = isSouvenirUnlocked(souvenirId, state.inventory?.unlockedSouvenirs);
    if (!hasUnlocked) {
      return { success: false, error: 'Bạn chưa mở khóa kỷ vật bang này!' };
    }

    // Check if already placed in room (accounting for all starter/legacy aliases)
    if (isSouvenirPlaced(normalizedSouvenirId, state.placedFurniture)) {
      return { success: false, error: 'Kỷ vật này đã được trưng bày trong phòng rồi!' };
    }

    let targetCol = col;
    let targetRow = row;
    if (targetCol === undefined || targetRow === undefined) {
      const spot = this.findFirstAvailableSpot(1, 1);
      if (!spot) {
        return { success: false, error: 'Phòng không còn ô trống để đặt kỷ vật!' };
      }
      targetCol = spot.col;
      targetRow = spot.row;
    }

    if (!this.canPlace(targetCol, targetRow, 1, 1)) {
      return { success: false, error: 'Ô này đã có đồ vật khác!' };
    }

    const newItem: PlacedFurniture = {
      id: `souvenir_${normalizedSouvenirId}_${Date.now()}`,
      typeId: 'souvenir',
      tier: 1,
      gridCol: targetCol,
      gridRow: targetRow,
      rotation: 0,
      customData: { souvenirId: normalizedSouvenirId }
    };

    state.placedFurniture.push(newItem);
    this.syncPassiveBuffs();
    GameState.triggerDebouncedSave();
    EventBus.emit('FURNITURE_UPDATED', state.placedFurniture);

    return { success: true, item: newItem };
  }

  /**
   * Moves an existing placed furniture piece to a new (newCol, newRow) grid coordinate.
   */
  public moveFurniture(id: string, newCol: number, newRow: number): boolean {
    const placed = GameState.getState().placedFurniture;
    const item = placed.find((f) => f.id === id);
    if (!item) return false;

    const dims = this.getEffectiveDimensions(item.typeId, item.rotation || 0);
    if (!this.canPlace(newCol, newRow, dims.width, dims.height, item.id)) {
      return false;
    }

    item.gridCol = newCol;
    item.gridRow = newRow;
    GameState.triggerDebouncedSave();
    EventBus.emit('FURNITURE_UPDATED', placed);
    return true;
  }

  /**
   * Toggles 90-degree isometric rotation on a placed furniture piece if valid at its location.
   */
  public rotateFurniture(id: string): boolean {
    const placed = GameState.getState().placedFurniture;
    const item = placed.find((f) => f.id === id);
    if (!item) return false;

    const nextRotation = item.rotation === 90 ? 0 : 90;
    const nextDims = this.getEffectiveDimensions(item.typeId, nextRotation);

    if (!this.canPlace(item.gridCol, item.gridRow, nextDims.width, nextDims.height, item.id)) {
      return false;
    }

    item.rotation = nextRotation;
    GameState.triggerDebouncedSave();
    EventBus.emit('FURNITURE_UPDATED', placed);
    return true;
  }

  /**
   * Upgrades a placed furniture piece from Tier 1 -> Tier 2 -> Tier 3.
   */
  public upgradeFurniture(id: string): {
    success: boolean;
    newTier?: number;
    cost?: number;
    error?: string;
  } {
    const state = GameState.getState();
    const item = state.placedFurniture.find((f) => f.id === id);
    if (!item) {
      return { success: false, error: 'Không tìm thấy nội thất cần nâng cấp!' };
    }

    if (item.typeId === 'souvenir') {
      return { success: false, error: 'Kỷ vật tiểu bang là độc bản, không cần nâng cấp!' };
    }

    const nextTier = (item.tier || 1) + 1;
    if (nextTier > 3) {
      return { success: false, error: 'Nội thất này đã đạt cấp tối đa (Tier 3)!' };
    }

    const nextCatalog = this.getCatalogItem(item.typeId, nextTier);
    if (!nextCatalog) {
      return { success: false, error: 'Không tìm thấy thông tin cấp tiếp theo!' };
    }

    if (state.stats.money < nextCatalog.costUSD) {
      return {
        success: false,
        cost: nextCatalog.costUSD,
        error: `Cần $${nextCatalog.costUSD} USD để nâng cấp lên ${nextCatalog.vietnameseName}!`
      };
    }

    GameState.modifyMoney(-nextCatalog.costUSD);
    item.tier = nextTier;
    this.syncPassiveBuffs();
    GameState.triggerDebouncedSave();
    EventBus.emit('FURNITURE_UPDATED', state.placedFurniture);

    return {
      success: true,
      newTier: nextTier,
      cost: nextCatalog.costUSD
    };
  }

  /**
   * Removes an optional decor item (plant, souvenir, or duplicate furniture) from the room.
   */
  public removeFurniture(id: string): { success: boolean; error?: string } {
    const state = GameState.getState();
    const idx = state.placedFurniture.findIndex((f) => f.id === id);
    if (idx === -1) {
      return { success: false, error: 'Không tìm thấy đồ vật!' };
    }

    const item = state.placedFurniture[idx];
    // Ensure at least 1 core furniture of bed/fridge/desk/sofa remains so player isn't soft-locked
    if (['bed', 'fridge', 'desk', 'sofa'].includes(item.typeId)) {
      const sameTypeCount = state.placedFurniture.filter((f) => f.typeId === item.typeId).length;
      if (sameTypeCount <= 1) {
        return {
          success: false,
          error: 'Không thể cất món đồ sinh hoạt cốt lõi duy nhất trong phòng!'
        };
      }
    }

    state.placedFurniture.splice(idx, 1);
    this.syncPassiveBuffs();
    GameState.triggerDebouncedSave();
    EventBus.emit('FURNITURE_UPDATED', state.placedFurniture);
    return { success: true };
  }

  /**
   * Aggregates passive Energy and Happiness recovery rates from all placed furniture and souvenirs,
   * clamped to balance caps (+3.0%/min Energy, +5.0%/min Happiness).
   */
  public calculatePassiveBuffs(): {
    passiveEnergyPerMin: number;
    passiveHappinessPerMin: number;
  } {
    const placed = GameState.getState()?.placedFurniture || [];
    let energyRate = 0;
    let happinessRate = 0;

    for (const item of placed) {
      if (item.typeId === 'souvenir') {
        const rawSouvenirId = item.customData?.souvenirId || 'souvenir_ny_statue_liberty';
        const souvEntry = getSouvenirEntry(rawSouvenirId);
        if (souvEntry) {
          energyRate += souvEntry.buff.passiveEnergyPerMin;
          happinessRate += souvEntry.buff.passiveHappinessPerMin;
        } else {
          const souvenirId = normalizeSouvenirId(rawSouvenirId);
          const stateEntry = (Object.values(FLAGSHIP_STATES) as any[]).find(
            (s) => s.souvenirId === souvenirId || s.souvenir?.id === souvenirId
          );
          if (stateEntry) {
            const buffType = stateEntry.souvenirBuff?.type || stateEntry.souvenir?.buff?.type;
            if (buffType === 'ENERGY_PASSIVE') {
              energyRate += 0.15;
            } else if (buffType === 'HAPPINESS_PASSIVE') {
              happinessRate += 0.25;
            } else {
              happinessRate += 0.15;
            }
          } else {
            happinessRate += 0.2;
          }
        }
        continue;
      }

      const catalogEntry = this.getCatalogItem(item.typeId, item.tier || 1);
      if (catalogEntry) {
        energyRate += catalogEntry.passiveEnergyBuff;
        happinessRate += catalogEntry.passiveHappinessBuff;
      }
    }

    return {
      passiveEnergyPerMin: Math.min(3.0, Math.round(energyRate * 100) / 100),
      passiveHappinessPerMin: Math.min(5.0, Math.round(happinessRate * 100) / 100)
    };
  }

  public syncPassiveBuffs(): void {
    const { passiveEnergyPerMin, passiveHappinessPerMin } = this.calculatePassiveBuffs();
    const engine = GameState.getSurvivalEngine();
    if (engine) {
      engine.setPassiveDecorRates(passiveEnergyPerMin, passiveHappinessPerMin);
    }
  }

  /**
   * Computes active interactive gameplay bonuses granted by placed Tier 1/2/3 furniture.
   */
  public getActiveFurnitureBonuses(
    stateOverride?: { placedFurniture?: PlacedFurniture[] } | null
  ): ActiveFurnitureBonuses {
    const placed = (stateOverride ?? GameState.getState())?.placedFurniture || [];
    let highestBedTier = 1;
    let highestDeskTier = 1;
    let highestFridgeTier = 1;
    let highestSofaTier = 1;
    let highestPlantTier = 0;
    let plantCheckinCashBonus = 0;

    for (const item of placed) {
      const tier = item.tier || 1;
      if (item.typeId === 'bed') {
        highestBedTier = Math.max(highestBedTier, tier);
      } else if (item.typeId === 'desk') {
        highestDeskTier = Math.max(highestDeskTier, tier);
      } else if (item.typeId === 'fridge') {
        highestFridgeTier = Math.max(highestFridgeTier, tier);
      } else if (item.typeId === 'sofa') {
        highestSofaTier = Math.max(highestSofaTier, tier);
      } else if (item.typeId === 'plant') {
        highestPlantTier = Math.max(highestPlantTier, tier);
        if (tier >= 3) plantCheckinCashBonus += 80;
        else if (tier === 2) plantCheckinCashBonus += 35;
        else plantCheckinCashBonus += 15;
      }
    }

    const deskPayMultiplierBonus = highestDeskTier >= 3 ? 0.28 : highestDeskTier === 2 ? 0.12 : 0;
    const deskStudyEnergyDiscount = highestDeskTier >= 3 ? 0.30 : highestDeskTier === 2 ? 0.15 : 0;

    const bedNapEnergyRestore = highestBedTier >= 3 ? 75 : highestBedTier === 2 ? 55 : 40;
    const bedNapHappinessBonus = highestBedTier >= 3 ? 10 : highestBedTier === 2 ? 8 : 5;
    const bedSleepEnergyRestore = highestBedTier >= 2 ? 100 : 85;
    const bedSleepHungerDrain = highestBedTier >= 3 ? 0 : highestBedTier === 2 ? -5 : -10;
    const bedWakeUpPayBuff = highestBedTier >= 3;

    const fridgePriceDiscount = highestFridgeTier >= 3 ? 0.30 : highestFridgeTier === 2 ? 0.15 : 0;
    const fridgeBuffDurationMultiplier = highestFridgeTier >= 3 ? 1.50 : highestFridgeTier === 2 ? 1.25 : 1.0;

    const sofaSitHappinessRestore = highestSofaTier >= 3 ? 40 : highestSofaTier === 2 ? 25 : 15;
    const sofaSitEnergyRestore = highestSofaTier >= 3 ? 15 : highestSofaTier === 2 ? 10 : 5;
    const sofaTvHappinessRestore = highestSofaTier >= 3 ? 70 : highestSofaTier === 2 ? 50 : 35;
    const sofaObstacleCostDiscount = highestSofaTier >= 3 ? 0.50 : highestSofaTier === 2 ? 0.25 : 0;

    return {
      highestBedTier,
      highestDeskTier,
      highestFridgeTier,
      highestSofaTier,
      highestPlantTier,
      deskPayMultiplierBonus,
      deskShiftPayMultiplier: 1 + deskPayMultiplierBonus,
      deskStudyEnergyDiscount,
      bedNapEnergyRestore,
      bedNapHappinessBonus,
      bedSleepEnergyRestore,
      bedSleepHungerDrain,
      bedWakeUpPayBuff,
      fridgePriceDiscount,
      fridgeFoodPriceDiscount: fridgePriceDiscount,
      fridgeBuffDurationMultiplier,
      fridgeFoodBuffDurationMultiplier: fridgeBuffDurationMultiplier,
      sofaSitHappinessRestore,
      sofaSitEnergyRestore,
      sofaTvHappinessRestore,
      sofaObstacleCostDiscount,
      sofaObstacleCostReduction: sofaObstacleCostDiscount,
      plantCheckinCashBonus: Math.min(240, plantCheckinCashBonus),
      plantCheckinBonusUSD: Math.min(240, plantCheckinCashBonus)
    };
  }

  private sessionSnapshot: {
    placedFurniture: PlacedFurniture[];
    money: number;
  } | null = null;

  /**
   * Captures a deep snapshot of placed furniture and player money when entering Decor Mode
   * so changes can be either committed or discarded when leaving Decor Mode.
   */
  public beginDecorSession(): void {
    const state = GameState.getState();
    if (!state) return;
    this.sessionSnapshot = {
      placedFurniture: JSON.parse(JSON.stringify(state.placedFurniture || [])),
      money: state.stats.money
    };
  }

  public hasActiveDecorSession(): boolean {
    return this.sessionSnapshot !== null;
  }

  public hasUnsavedChanges(): boolean {
    if (!this.sessionSnapshot) return false;
    const state = GameState.getState();
    if (!state) return false;
    const currentJson = JSON.stringify(state.placedFurniture || []);
    const snapJson = JSON.stringify(this.sessionSnapshot.placedFurniture);
    return currentJson !== snapJson || state.stats.money !== this.sessionSnapshot.money;
  }

  /**
   * Commits any changes made during Decor Mode and clears the snapshot.
   */
  public commitDecorSession(): void {
    this.sessionSnapshot = null;
    this.syncPassiveBuffs();
    GameState.triggerDebouncedSave();
  }

  /**
   * Discards all changes made during Decor Mode, restoring furniture layout and refunding spent USD.
   */
  public discardDecorSession(): boolean {
    if (!this.sessionSnapshot) return false;
    const state = GameState.getState();
    if (!state) {
      this.sessionSnapshot = null;
      return false;
    }

    state.placedFurniture = JSON.parse(JSON.stringify(this.sessionSnapshot.placedFurniture));

    const moneyDiff = this.sessionSnapshot.money - state.stats.money;
    if (moneyDiff !== 0) {
      GameState.modifyMoney(moneyDiff);
    }

    this.sessionSnapshot = null;
    this.syncPassiveBuffs();
    GameState.triggerDebouncedSave();
    EventBus.emit('FURNITURE_UPDATED', state.placedFurniture);
    return true;
  }
}

export const DecorSystem = DecorSystemManager.getInstance();

